import React from 'react';
import { Box } from '../../../../base/Box/Box';
import { PortfolioCarouselCardStoryblok } from '../../../../types/storyblok';
import { Type } from '../../../../base/Type/Type';
import Markup from '../../../../base/Markup/Markup';
import Link from '../../../../base/Link/Link';
import { SplideSlide } from '@splidejs/react-splide';
import { StoryblokImage } from '../../../../base/StoryblokImage/StoryblokImage';
import classNames from 'classnames';

const ensureLinkValid = (link: string) =>
  !link.startsWith('https') ? `https://${link}` : link;

const enum TagColors {
  Yellow = 'yellow',
  Black = 'black',
}

const colorClasses = {
  [TagColors.Yellow]: 'ffYellow',
  [TagColors.Black]: 'ffBlack',
};

const backgroundColorClasses = {
  [TagColors.Yellow]: '!bg-ffYellow',
  [TagColors.Black]: '!bg-ffBlack',
};

const textColorClasses = {
  [TagColors.Yellow]: '!text-ffYellow',
  [TagColors.Black]: '!text-ffBlack',
};

const getTagColor = ({
  tagColor,
}: PortfolioCarouselCardStoryblok): TagColors => {
  const color = tagColor as TagColors;

  return color || TagColors.Yellow;
};

export const PortfolioCarouselCard = ({
  company,
  fundingRound,
  description,
  linkTitle,
  link,
  image,
  ...rest
}: PortfolioCarouselCardStoryblok) => {
  const targetLink = link?.url || link?.cached_url || '';
  const tagColor = getTagColor(rest);

  if (
    !company &&
    !fundingRound &&
    !description &&
    !linkTitle &&
    !link &&
    !image
  ) {
    return null;
  }

  return (
    <SplideSlide>
      <Box
        as="article"
        className=" h-full rounded border px-4 py-5"
        border
        insetBkg
        overlayOverBkg
      >
        <Box as="div" className="flex h-full flex-col">
          <StoryblokImage
            src={image}
            alt={company}
            mode="responsive"
            maxWidth={1200}
          />

          <Box as="div" className="flex h-full flex-col justify-between">
            <Box as="div">
              <Box as="div" className="mb-3 mt-5 flex flex-row flex-wrap">
                <Type as="p" className="text-h4 !font-light">
                  {company}
                </Type>
                <Type as="p" className="text-h4 !font-light !text-ffMidGrey">
                  {fundingRound && <Type as="span">&nbsp;/&nbsp;</Type>}
                  {fundingRound}
                </Type>
              </Box>

              <Markup
                content={description}
                className="mb-4 mt-3 text-p1 sm:mb-8 md:mb-12 lg:mb-16"
              />
            </Box>
            <Box
              as="div"
              className="relative flex flex-row flex-wrap items-center justify-between "
            >
              <Box as="div" className="flex h-full items-center">
                <div
                  className={classNames(
                    "mr-2 h-4 w-4 flex-shrink-0 rounded-full content-['']",
                    backgroundColorClasses[tagColor]
                  )}
                />
                <Type
                  as="p"
                  className={classNames('text-p3', textColorClasses[tagColor])}
                >
                  {linkTitle}
                </Type>
              </Box>
              {!!targetLink && (
                <Link
                  href={ensureLinkValid(targetLink)}
                  className="hover:opacity-80 hover:translate-x-1"
                  variant="naked-arrow-right"
                  empty
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </SplideSlide>
  );
};

export default PortfolioCarouselCard;
